import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { BarChart3, ChevronRight, Download, Filter, Printer, Sheet } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { PageHeader } from "@/components/PageHeader";
import { EmptyState, LoadingState } from "@/components/states";
import { EngagementDashboard } from "@/components/EngagementDashboard";
import { AnnouncementRecipients } from "@/components/AnnouncementRecipients";
import { SavedEventViews } from "@/components/SavedEventViews";
import { buildEngagementTable, type EventLogFilters } from "@/lib/teacher-engagement";
import {
  listTeacherClasses,
  listClassEventLog,
  getAnnouncementEngagement,
  type ClassRow,
} from "@/lib/teacher.functions";
import { listClassAssignments } from "@/lib/teacher-content.functions";
import { downloadCsv, downloadXlsx, printResults } from "@/lib/teacher-export";

export const Route = createFileRoute("/_app/teacher-results")({
  ssr: false,
  head: () => ({
    meta: [
      { title: "Risultati della classe — Lumen" },
      {
        name: "description",
        content: "Consegne, valutazioni e medie delle attività assegnate alle tue classi virtuali.",
      },
      { property: "og:title", content: "Risultati della classe — Lumen" },
      {
        property: "og:description",
        content: "Medie, consegne e progressi delle attività assegnate.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
  }),
  component: TeacherResults,
});

type Stat = { submitted: number; graded: number; avg: number | null };
type AssignmentRow = {
  id: string;
  title: string;
  kind: string;
  max_score: number;
  due_date: string | null;
};
type LogRow = { id: string; event: string; actor: string; createdAt: string };
type Engagement = Awaited<ReturnType<typeof getAnnouncementEngagement>>;
const EVENTS = [
  "announcement_created",
  "announcement_updated",
  "announcement_deleted",
  "announcement_delivered",
  "announcement_read",
] as const;

function TeacherResults() {
  const { t } = useTranslation();
  const classesFn = useServerFn(listTeacherClasses);
  const assignmentsFn = useServerFn(listClassAssignments);
  const logFn = useServerFn(listClassEventLog);
  const engagementFn = useServerFn(getAnnouncementEngagement);

  const [log, setLog] = useState<LogRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [classes, setClasses] = useState<ClassRow[]>([]);
  const [classId, setClassId] = useState<string | null>(null);
  const [rows, setRows] = useState<AssignmentRow[]>([]);
  const [stats, setStats] = useState<Record<string, Stat>>({});
  const [busy, setBusy] = useState(false);
  const [engagement, setEngagement] = useState<Engagement | null>(null);
  const [fEvent, setFEvent] = useState<string>("");
  const [fFrom, setFFrom] = useState("");
  const [fTo, setFTo] = useState("");
  const [fQuery, setFQuery] = useState("");
  const [openPost, setOpenPost] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const list = await classesFn({ data: undefined } as never);
        if (!alive) return;
        setClasses(list);
        setClassId(list[0]?.id ?? null);
      } catch {
        toast.error(t("states.error.title", { defaultValue: "Something went wrong" }));
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!classId) return;
    let alive = true;
    setBusy(true);
    (async () => {
      try {
        const res = await assignmentsFn({ data: { classId } });
        if (!alive) return;
        setRows(res.assignments as unknown as AssignmentRow[]);
        setStats(res.stats as Record<string, Stat>);
        try {
          const eng = (await engagementFn({ data: { classId } })) as Engagement;
          if (alive) setEngagement(eng);
        } catch {
          if (alive) setEngagement(null);
        }
      } catch {
        toast.error(t("states.error.title", { defaultValue: "Something went wrong" }));
      } finally {
        if (alive) setBusy(false);
      }
    })();
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [classId]);

  useEffect(() => {
    if (!classId) return;
    let alive = true;
    const timer = setTimeout(async () => {
      try {
        const events = (await logFn({
          data: {
            classId,
            event: fEvent || null,
            from: fFrom ? new Date(fFrom).toISOString() : null,
            to: fTo ? new Date(`${fTo}T23:59:59`).toISOString() : null,
            q: fQuery || null,
          },
        })) as LogRow[];
        if (alive) setLog(events);
      } catch {
        if (alive) setLog([]);
      }
    }, 250);
    return () => {
      alive = false;
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [classId, fEvent, fFrom, fTo, fQuery]);

  if (loading) return <LoadingState />;

  const graded = rows.filter((r) => (stats[r.id]?.graded ?? 0) > 0);
  const classAvg = graded.length
    ? Math.round((graded.reduce((a, r) => a + (stats[r.id]?.avg ?? 0), 0) / graded.length) * 10) /
      10
    : null;
  const totalSubmitted = rows.reduce((a, r) => a + (stats[r.id]?.submitted ?? 0), 0);
  const currentClassName = classes.find((c) => c.id === classId)?.name ?? "—";
  const engagementExport = engagement
    ? {
        posts: engagement.posts,
        audience: engagement.audience,
        topReaders: engagement.topReaders,
        labels: {
          section: t("teacher.engagement.title"),
          announcement: t("teacher.engagement.announcement", { defaultValue: "Avviso" }),
          date: t("teacher.results.due"),
          delivered: t("teacher.engagement.delivered", { defaultValue: "Consegnati" }),
          reads: t("teacher.engagement.readsCol", { defaultValue: "Letture" }),
          readRate: t("teacher.engagement.readRateCol", { defaultValue: "% letture" }),
          avgTime: t("teacher.engagement.avgTimeCol", { defaultValue: "Tempo medio lettura" }),
          topReaders: t("teacher.engagement.topReaders"),
        },
      }
    : null;
  const exportPayload = {
    className: currentClassName,
    assignments: rows,
    stats,
    engagement: engagementExport,
  };
  const applyView = (v: EventLogFilters) => {
    if (v.classId && classes.some((c) => c.id === v.classId)) setClassId(v.classId);
    setFEvent(v.event ?? "");
    setFQuery(v.author ?? "");
    setFFrom(v.from ?? "");
    setFTo(v.to ?? "");
  };
  const exportHeaders = {
    title: t("teacher.results.activities"),
    kind: t("teacher.results.kind"),
    due: t("teacher.results.due"),
    submitted: t("teacher.results.submissions"),
    graded: t("teacher.results.gradedCol"),
    average: t("teacher.results.average"),
    max: t("teacher.results.maxScore"),
    percent: t("teacher.results.percent"),
  };

  return (
    <main className="space-y-5 px-4 py-6">
      <PageHeader title={t("teacher.results.title")} subtitle={t("teacher.results.subtitle")} />

      {classes.length === 0 ? (
        <EmptyState title={t("teacher.dash.empty")} />
      ) : (
        <>
          <div className="flex flex-wrap gap-2">
            {classes.map((c) => (
              <Button
                key={c.id}
                size="sm"
                variant={c.id === classId ? "default" : "secondary"}
                className="min-h-10 rounded-full"
                onClick={() => setClassId(c.id)}
              >
                {c.name}
              </Button>
            ))}
          </div>

          <div className="grid grid-cols-3 gap-3">
            <Card className="card-soft border-0 p-4">
              <p className="text-xs text-muted-foreground">{t("teacher.results.activities")}</p>
              <p className="text-xl font-bold">{rows.length}</p>
            </Card>
            <Card className="card-soft border-0 p-4">
              <p className="text-xs text-muted-foreground">{t("teacher.results.submissions")}</p>
              <p className="text-xl font-bold">{totalSubmitted}</p>
            </Card>
            <Card className="card-soft border-0 p-4">
              <p className="text-xs text-muted-foreground">{t("teacher.results.average")}</p>
              <p className="text-xl font-bold">{classAvg == null ? "—" : classAvg}</p>
            </Card>
          </div>

          {rows.length > 0 && (
            <div className="flex flex-wrap gap-2">
              <Button
                size="sm"
                variant="secondary"
                className="min-h-10 rounded-full"
                onClick={() => {
                  downloadCsv({ ...exportPayload, headers: exportHeaders });
                  toast.success(t("teacher.results.exported"));
                }}
              >
                <Download className="mr-1 h-4 w-4" aria-hidden />
                {t("teacher.results.exportCsv")}
              </Button>
              <Button
                size="sm"
                variant="secondary"
                className="min-h-10 rounded-full"
                onClick={() => {
                  downloadXlsx(
                    { ...exportPayload, headers: exportHeaders },
                    {
                      [t("teacher.results.activities")]: String(rows.length),
                      [t("teacher.results.submissions")]: String(totalSubmitted),
                      [t("teacher.results.average")]: classAvg == null ? "—" : String(classAvg),
                    },
                  );
                  toast.success(t("teacher.results.exported"));
                }}
              >
                <Sheet className="mr-1 h-4 w-4" aria-hidden />
                {t("teacher.results.exportXlsx")}
              </Button>
              <Button
                size="sm"
                variant="secondary"
                className="min-h-10 rounded-full"
                onClick={() => {
                  const ok = printResults(
                    { ...exportPayload, headers: exportHeaders },
                    t("teacher.results.title"),
                  );
                  if (!ok) toast.error(t("teacher.results.popupBlocked"));
                }}
              >
                <Printer className="mr-1 h-4 w-4" aria-hidden />
                {t("teacher.results.exportPdf")}
              </Button>
            </div>
          )}

          {busy ? (
            <LoadingState />
          ) : rows.length === 0 ? (
            <EmptyState title={t("teacher.results.empty")} />
          ) : (
            <section className="space-y-3">
              {rows.map((r) => {
                const s = stats[r.id] ?? { submitted: 0, graded: 0, avg: null };
                const pct =
                  s.avg == null
                    ? 0
                    : Math.max(0, Math.min(100, (s.avg / (r.max_score || 100)) * 100));
                return (
                  <Card key={r.id} className="card-soft space-y-2 border-0 p-4">
                    <div className="flex flex-wrap items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="break-words text-sm font-semibold leading-snug">{r.title}</p>
                        <p className="text-xs text-muted-foreground">
                          {t("teacher.assign.submitted", { n: s.submitted })} ·{" "}
                          {t("teacher.assign.graded", { n: s.graded })}
                        </p>
                      </div>
                      <Badge variant="secondary" className="shrink-0">
                        {s.avg == null ? t("teacher.results.notGraded") : `${s.avg}/${r.max_score}`}
                      </Badge>
                    </div>
                    <div className="h-2 overflow-hidden rounded-full bg-muted" aria-hidden>
                      <div
                        className="h-full rounded-full bg-primary transition-all"
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                  </Card>
                );
              })}
              {classId && (
                <Link
                  to="/class/$classId"
                  params={{ classId }}
                  className="card-soft flex items-center justify-between rounded-3xl border-0 bg-card p-4 text-sm font-semibold"
                >
                  <span className="inline-flex items-center gap-2">
                    <BarChart3 className="h-4 w-4 text-primary" aria-hidden />
                    {t("teacher.results.openClass")}
                  </span>
                  <ChevronRight className="h-4 w-4 text-muted-foreground" aria-hidden />
                </Link>
              )}
            </section>
          )}

          {engagement && engagement.posts.length > 0 && classId && (
            <>
              <EngagementDashboard
                className={currentClassName}
                data={engagement}
                onOpenPost={setOpenPost}
                onExportCsv={() => {
                  downloadCsv({ ...exportPayload, headers: exportHeaders });
                  toast.success(t("teacher.results.exported"));
                }}
                onExportPdf={() => {
                  const ok = printResults(
                    { ...exportPayload, headers: exportHeaders },
                    t("teacher.engagement.title"),
                  );
                  if (!ok) toast.error(t("teacher.results.popupBlocked"));
                }}
              />
              <AnnouncementRecipients
                classId={classId}
                postId={openPost}
                onClose={() => setOpenPost(null)}
              />
            </>
          )}

          <section className="space-y-2">
            <h2 className="text-sm font-semibold">{t("teacher.audit.title")}</h2>
            <p className="text-xs text-muted-foreground break-words">
              {t("teacher.audit.subtitle")}
            </p>
            <Card className="card-soft space-y-2 border-0 p-4">
              <div className="flex items-center gap-2 text-xs font-semibold">
                <Filter className="h-3.5 w-3.5 text-primary" aria-hidden />
                {t("teacher.audit.filters")}
              </div>
              <Input
                value={fQuery}
                onChange={(e) => setFQuery(e.target.value)}
                placeholder={t("teacher.audit.searchPh")}
                aria-label={t("teacher.audit.searchPh")}
              />
              <div className="flex flex-wrap gap-2">
                <Button
                  size="sm"
                  variant={fEvent === "" ? "default" : "secondary"}
                  className="min-h-9 rounded-full"
                  onClick={() => setFEvent("")}
                >
                  {t("teacher.audit.allEvents")}
                </Button>
                {EVENTS.map((ev) => (
                  <Button
                    key={ev}
                    size="sm"
                    variant={fEvent === ev ? "default" : "secondary"}
                    className="min-h-9 rounded-full"
                    onClick={() => setFEvent(ev)}
                  >
                    {t(`teacher.audit.event.${ev}`, { defaultValue: ev })}
                  </Button>
                ))}
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Input
                  type="date"
                  value={fFrom}
                  onChange={(e) => setFFrom(e.target.value)}
                  aria-label={t("teacher.audit.from")}
                />
                <Input
                  type="date"
                  value={fTo}
                  onChange={(e) => setFTo(e.target.value)}
                  aria-label={t("teacher.audit.to")}
                />
              </div>
              <div className="border-t border-border/60 pt-3">
                <SavedEventViews
                  filters={{ classId, event: fEvent, author: fQuery, from: fFrom, to: fTo }}
                  classNameById={(id) => classes.find((c) => c.id === id)?.name ?? null}
                  onApply={applyView}
                />
              </div>
            </Card>
          </section>

          {log.length > 0 ? (
            <section className="space-y-2">
              <Card className="card-soft divide-y divide-border/60 border-0 p-0">
                {log.slice(0, 30).map((e) => (
                  <div key={e.id} className="flex items-start justify-between gap-3 px-4 py-2.5">
                    <p className="min-w-0 text-xs break-words">
                      <span className="font-semibold">{e.actor}</span> ·{" "}
                      {t(`teacher.audit.event.${e.event}`, { defaultValue: e.event })}
                    </p>
                    <span className="shrink-0 text-[11px] text-muted-foreground">
                      {new Date(e.createdAt).toLocaleString()}
                    </span>
                  </div>
                ))}
              </Card>
            </section>
          ) : (
            <EmptyState title={t("teacher.audit.empty")} />
          )}
        </>
      )}
    </main>
  );
}
